import { EntityState } from "@reduxjs/toolkit";

export enum UserType {
  MAIN = "main",
  SUB = "sub",
  FROZEN = "frozen",
}

export enum AccountType {
  GUEST = "guest",
  KIDS = "kids",
  PARENTS = "parents",
}

export enum UserProvider {
  GOOGLE = "google.com",
  APPLE = "apple.com",
  PASSWORD = "password",
}

// 容姿の型定義
export type GlassesType = "none" | "glasses";
export type AccessoryType = "megane" | "boushi" | "hairpin" | "ribbon" | "bandaid";
export type HairstyleType = "very_short" | "short" | "medium" | "slightly_long" | "long";
export type BodyTypeType = "thin" | "slightly_thin" | "medium" | "slightly_chubby" | "chubby";
export type FaceShapeType = "round" | "oval" | "oblong" | "square" | "triangle";

export type AppUser = {
  uid: string;
  type: UserType;
  active: boolean;
  account_type: AccountType;
  parent_uid: string;
  email: string;
  provider: string;
  display_name: string;
  birthday: string;
  gender: string;
  icon?: string;
  favorite_color?: string[];
  best_subject?: string[];
  glasses?: GlassesType;
  accessory?: AccessoryType[];
  hairstyle?: HairstyleType;
  body_type?: BodyTypeType;
  face_shape?: FaceShapeType;
  self_introduction?: string;
  created_at: string;
  updated_at: string;
  last_sign_in_time: string;
  last_sign_in_env: string;
  login_count: number;
};

export enum MessageType {
  USER_INITIAL = "user_initial",
  NORMAL = "normal",
  CONTACT = "contact",
  CONTACT_MESSAGE = "contact_message",
  CONTACT_INITIAL = "contact_initial",
  CONTACT_REPLY = "contact_reply",
  CONTACT_INPUT_AUTO_REPLY = "contact_input_auto_reply",
  KIDS_REVIEW_RATING = "kids_review_rating",
  KIDS_REVIEW_RATING_AUTO_REPLY = "kids_review_rating_auto_reply",
  KIDS_REVIEW_FEEDBACK = "kids_review_feedback",
  KIDS_REVIEW_FEEDBACK_REPLY = "kids_review_feedback_reply",
  KIDS_REVIEW_FEEDBACK_AUTO_REPLY = "kids_review_feedback_auto_reply",
  KIDS_REVIEW_FEEDBACK_INPUT_AUTO_REPLY = "kids_review_feedback_input_auto_reply",
}

export enum MessageContentType {
  TEXT = "text",
  IMAGE = "image",
  IMAGE_WITH_TEXT = "image_with_text",
  PROJECT = "project",
  COURSE_CLEAR = "course_clear",
}

export type FormMessageContact = {
  id: string;
  uid: string;
  type: string;
  detail: string;
  created_at: string;
};

export type FormMessageContactReply = {
  id: string;
  contact_id: string;
  uid: string;
  type: string;
  detail: string;
  created_at: string;
  date: string;
  title: string;
  content: string;
  published: boolean;
  published_at: string;
};

export type FormMessageKidsReviewRating = {
  id: string;
  uid: string;
  rating: number;
  created_at: string;
};

export type FormMessageKidsReviewFeedback = {
  id: string;
  uid: string;
  rating?: number;
  type: string;
  detail: string;
  created_at: string;
};

export type FormMessageKidsReviewFeedbackReply = {
  id: string;
  kids_review_feedback_id: string;
  uid: string;
  rating?: number;
  type: string;
  detail: string;
  created_at: string;
  title: string;
  content: string;
  published: boolean;
  published_at: string;
};

export type ChatRoomMessageSync = {
  id: string;
  uid: string;
  lastSyncAt: string;
  lastReadAt?: string;
  latestReadAt?: string;
  latestMessage?: ChatRoomMessage;
  latestMessageCreatedAt?: string;
};

export enum ChatRoomType {
  TALK = "talk",
  CONTACT = "contact",
  NOTIFICATION = "notification",
  AI_CHAT = "ai_chat",
}

export type ChatRoom = {
  id: string;
  uid: string;
  name: string;
  icon: string;
  type: ChatRoomType;
  description: string;
  last_read_at: string;
  created_at: string;
  updated_at: string;
  members: ChatRoomMember[];
};

export enum ChatRoomMemberType {
  KIDS = "kids",
  PARENTS = "parents",
  OFFICIAL = "official",
}
export type ChatRoomMember = {
  id: string;
  chat_room_id: string;
  uid: string;
  type: ChatRoomMemberType;
  last_read_at: string;
  created_at: string;
  updated_at: string;
};

export type ChatRoomMessage = {
  id: string;
  chat_room_id: string;
  user_uid: string;
  user_type: ChatRoomMemberType;
  message_type: MessageType;
  message_content_type: MessageContentType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  message_content: any;
  created_at: string;
  updated_at: string;
};

export type UserState = {
  parent: AppUser | null;
  active: AppUser | null;
  users: AppUser[];
  isRefreshed: boolean;
  status: {
    course: {
      finished: number;
      total: number;
    };
    project: {
      total: number;
      viewed: number;
      published: number;
      favorited: number;
    };
  };
  history: {
    period: string[];
    course: {
      total: {
        day: { date: string; finished: number }[];
        avg: { day: number };
      };
      new: {
        day: { date: string; finished: number }[];
      };
    };
    project: {
      favorited: {
        total: {
          day: { date: string; favorited: number }[];
          avg: { day: number };
        };
        new: {
          day: { date: string; favorited: number }[];
        };
      };
      viewed: {
        total: {
          day: { date: string; viewed: number }[];
        };
      };
    };
  };
  groupUsers: EntityState<AppUser>;
  selectedChatRoomId: string;
  chatRooms: EntityState<ChatRoom>;
  chatRoomMessages: EntityState<ChatRoomMessage>;
  chatRoomMessageSync: EntityState<ChatRoomMessageSync>;
};
